import React from 'react';
import classnames from 'classnames';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Tab from './Tab';
import type { TabItem, ItemIndexType } from './types';

export interface Props {
  viewClassNames: {
    root: string;
    tab: string;
    prefix?: string;
    label?: string;
    dropdown?: string;
    'active-tab': string;
  };
  width?: number;
  className?: string;
  items: TabItem[];
  activeIndex?: ItemIndexType;
  /** Use sparingly, when you need to add an extra non-standard tab */
  extraTabs?: React.ReactNode;
  onChange?: (index: ItemIndexType) => void;
  activeItemRef?: (node: HTMLElement | null) => void;

  /**
   * The tag type to use as the root container.
   *
   * Use this for a11y support.
   *
   * e.g. `tag='section'`, `tag='nav'`
   *
   * or with sectioning components from our da-a11y package
   *
   * e.g. `tag={Section}`, `tag={Nav}`
   */
  rootTag?: keyof React.JSX.IntrinsicElements | React.ComponentType;

  /** To add a place for screen reader users to hop to and to describe the tab list */
  a11yHeading?: string;
}

export const Tabs = React.forwardRef<any, Props>(
  (
    {
      width,
      className,
      viewClassNames: s,
      items,
      activeIndex,
      extraTabs,
      onChange,
      activeItemRef,
      rootTag: RootTag = 'div',
      a11yHeading,
    },
    ref
  ) => {
    return (
      <RootTag
        // @ts-ignore - Dynamic tags with ref passing is hard for typescript to understand
        className={classnames(s['root'], className)}
        style={{ width }}
        ref={ref}
      >
        {a11yHeading && (
          <VisuallyHidden>
            <Heading>{a11yHeading}</Heading>
          </VisuallyHidden>
        )}
        {items.map(item => (
          <Tab
            key={item.index}
            viewClassNames={s}
            label={
              <>
                {item.label}
                {item.labelExtras}
              </>
            }
            href={item.href}
            prefix={item.prefix}
            postfix={item.tabExtras}
            dropdown={item.tabDropdown}
            biData={item.biData}
            isActive={item.index === activeIndex}
            data-hook={item.dataHook}
            onClick={() => {
              onChange && onChange(item.index);
              item.onClick && item.onClick();
            }}
            innerRef={item.index === activeIndex ? activeItemRef : undefined}
          />
        ))}
        {extraTabs}
      </RootTag>
    );
  }
);
Tabs.displayName = 'Tabs';

export default Tabs;
